import { sortBy } from 'lodash';
import i18next from 'i18next';
import moment from 'moment-timezone';

import { convertEntityQuantityToInpulseUnit, convertUnits } from '@commons/utils/conversion';
import { DATE_DISPLAY_FORMATS } from '@commons/DatePickers/constants';
import { formatNumberToExcelUsage } from '@commons/DisplayNumber';
import { TIME_DISPLAY_FORMATS } from '@commons/DatePickers/constants';
import utilsXLS from '@commons/utils/makeXLS';

const getInformationsSheetColumns = (clientStoreName) => [
  {
    name: clientStoreName,
    propertyKey: 'storeName',
  },
  {
    name: i18next.t('GENERAL.DATE'),
    propertyKey: 'selectedDate',
  },
];

const getProductionPlanningCommonsData = () => [
  {
    name: i18next.t('GENERAL.NAME'),
    propertyKey: 'name',
    width: 100,
  },
  {
    name: i18next.t('GENERAL.CATEGORY'),
    propertyKey: 'category',
    transform: (value) => value || i18next.t('GENERAL.NONE_VALUE'),
  },
];

const getProductSheetColums = (timeSlots) => {
  const columns = getProductionPlanningCommonsData();

  timeSlots.forEach(({ startTime, endTime, id, name }) => {
    const formattedStartTime = moment(
      startTime,
      TIME_DISPLAY_FORMATS.SLASHED_HOUR_MINUTE_SECOND,
    ).format(TIME_DISPLAY_FORMATS.SLASHED_HOUR_MINUTE);

    const formattedEndTime = moment(
      endTime,
      TIME_DISPLAY_FORMATS.SLASHED_HOUR_MINUTE_SECOND,
    ).format(TIME_DISPLAY_FORMATS.SLASHED_HOUR_MINUTE);

    const isDefaultTimeslot = !name;

    columns.push(
      {
        name: isDefaultTimeslot
          ? i18next.t('PRODUCTION.PLANNING.EXPORT_TIMESLOT_FORECAST_COLUMN_NAME_NO_TIME')
          : i18next.t('PRODUCTION.PLANNING.EXPORT_TIMESLOT_FORECAST_COLUMN_NAME', {
              startTime: formattedStartTime,
              endTime: formattedEndTime,
            }),
        propertyKey: `${id}-forecast`,
      },
      {
        name: isDefaultTimeslot
          ? i18next.t('PRODUCTION.PLANNING.EXPORT_TIMESLOT_STOCK_COLUMN_NAME_NO_TIME')
          : i18next.t('PRODUCTION.PLANNING.EXPORT_TIMESLOT_STOCK_COLUMN_NAME', {
              startTime: formattedStartTime,
              endTime: formattedEndTime,
            }),
        propertyKey: `${id}-inStock`,
      },
      {
        name: isDefaultTimeslot
          ? i18next.t('PRODUCTION.PLANNING.EXPORT_TIMESLOT_TO_PRODUCE_COLUMN_NAME_NO_TIME')
          : i18next.t('PRODUCTION.PLANNING.EXPORT_TIMESLOT_TO_PRODUCE_COLUMN_NAME', {
              startTime: formattedStartTime,
              endTime: formattedEndTime,
            }),
        propertyKey: `${id}-toProduce`,
      },
      {
        name: isDefaultTimeslot
          ? i18next.t('PRODUCTION.PLANNING.EXPORT_TIMESLOT_PRODUCED_COLUMN_NAME_NO_TIME')
          : i18next.t('PRODUCTION.PLANNING.EXPORT_TIMESLOT_PRODUCED_COLUMN_NAME', {
              startTime: formattedStartTime,
              endTime: formattedEndTime,
            }),
        propertyKey: `${id}-produced`,
      },
    );
  });

  return columns;
};

const getEntitiesSheetColumns = (timeSlots) => {
  const columns = [
    ...getProductionPlanningCommonsData(),
    {
      name: i18next.t('GENERAL.UNIT'),
      propertyKey: 'unit',
    },
  ];

  timeSlots.forEach(({ startTime, endTime, id, name }) => {
    const formattedStartTime = moment(
      startTime,
      TIME_DISPLAY_FORMATS.SLASHED_HOUR_MINUTE_SECOND,
    ).format(TIME_DISPLAY_FORMATS.SLASHED_HOUR_MINUTE);

    const formattedEndTime = moment(
      endTime,
      TIME_DISPLAY_FORMATS.SLASHED_HOUR_MINUTE_SECOND,
    ).format(TIME_DISPLAY_FORMATS.SLASHED_HOUR_MINUTE);

    const isDefaultTimeslot = !name;

    columns.push({
      name: isDefaultTimeslot
        ? i18next.t('PRODUCTION.PLANNING.EXPORT_TIMESLOT_TO_PRODUCE_COLUMN_NAME_NO_TIME')
        : i18next.t('PRODUCTION.PLANNING.EXPORT_TIMESLOT_TO_PRODUCE_COLUMN_NAME', {
            startTime: formattedStartTime,
            endTime: formattedEndTime,
          }),
      propertyKey: `${id}-toProduce`,
    });
  });

  return columns;
};

const getFormattedProducts = (products) => {
  const formattedProducts = products.map((product) => {
    const formattedProduct = {
      ...product,
    };

    Object.entries(product.dataByTimeSlotIds).forEach(
      ([timeSlotId, { forecast, inStock, produced }]) => {
        formattedProduct[`${timeSlotId}-forecast`] = forecast >= 0 ? forecast : null;
        formattedProduct[`${timeSlotId}-inStock`] = inStock >= 0 ? inStock : null;
        formattedProduct[`${timeSlotId}-produced`] = produced >= 0 ? produced : null;

        const toProduce = forecast - inStock >= 0 ? forecast - inStock : 0;

        formattedProduct[`${timeSlotId}-toProduce`] = toProduce;
      },
    );

    return formattedProduct;
  });

  return sortBy(formattedProducts, 'name');
};

const getFormattedEntities = (entities) => {
  const formattedEntities = entities.map((entity) => {
    const formattedEntity = {
      ...entity,
    };

    Object.entries(entity.dataByTimeSlotIds).forEach(([timeSlotId, timeSlotData]) => {
      const toProduce =
        timeSlotData.toProduce > 0
          ? convertEntityQuantityToInpulseUnit(timeSlotData.toProduce, entity.unit)
          : 0;

      formattedEntity[`${timeSlotId}-toProduce`] = formatNumberToExcelUsage(toProduce, 2);
    });

    return { ...formattedEntity, unit: convertUnits(entity.unit, false) };
  });

  return sortBy(formattedEntities, 'name');
};

export const exportProductionPlanning = (
  products,
  recipes,
  ingredients,
  timeSlots,
  selectedStoreName,
  selectedDate,
  clientStoreName,
) => {
  const sortedTimeSlots = sortBy(timeSlots, 'startTime');

  const productSheetColumns = getProductSheetColums(sortedTimeSlots);
  const recipeSheetColumns = getEntitiesSheetColumns(sortedTimeSlots);
  const ingredientSheetColumns = getEntitiesSheetColumns(sortedTimeSlots);
  const informationsSheetColumns = getInformationsSheetColumns(clientStoreName);

  const formattedProducts = getFormattedProducts(products);
  const formattedRecipes = getFormattedEntities(recipes);
  const formattedIngredients = getFormattedEntities(ingredients);

  const productSheet = utilsXLS.generateDefaultSheet(
    i18next.t('GENERAL.PRODUCT_PLURAL'),
    productSheetColumns,
    formattedProducts,
    { numberDecimals: 2 },
    {
      cellsWidth: Array(productSheetColumns.length).fill({ wpx: 130 }),
    },
  );

  const recipeSheet = utilsXLS.generateDefaultSheet(
    i18next.t('GENERAL.RECIPE_PLURAL'),
    recipeSheetColumns,
    formattedRecipes,
    { numberDecimals: 2 },
    {
      cellsWidth: Array(recipeSheetColumns.length).fill({ wpx: 130 }),
    },
  );

  const ingredientSheet = utilsXLS.generateDefaultSheet(
    i18next.t('GENERAL.INGREDIENT_PLURAL'),
    ingredientSheetColumns,
    formattedIngredients,
    { numberDecimals: 2 },
    {
      cellsWidth: Array(ingredientSheetColumns.length).fill({ wpx: 130 }),
    },
  );

  const formattedDate = moment(selectedDate).format(DATE_DISPLAY_FORMATS.SLASHED_DAY_MONTH_YEAR);

  const informationsSheet = utilsXLS.generateDefaultSheet(
    i18next.t('GENERAL.INFORMATIONS'),
    informationsSheetColumns,
    [{ storeName: selectedStoreName, selectedDate: formattedDate }],
  );

  utilsXLS.makeXLS(
    i18next.t('PRODUCTION.PLANNING.EXPORT_FILENAME', {
      storeName: selectedStoreName,
      date: formattedDate,
    }),
    [productSheet, recipeSheet, ingredientSheet, informationsSheet],
  );
};
